import { useState, useCallback } from "react";
import { useBackButton } from "@/hooks/useBackButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "@/integrations/supabase/client";
import { QrCode, CreditCard, CheckCircle, Copy, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const PIX_KEY = "18827317783";
const INSURANCE_PRICE = 49.99;

const creditRates: Record<number, number> = {
  1: 3.15, 2: 5.39, 3: 6.12, 4: 6.85, 5: 7.57, 6: 8.28,
  7: 8.99, 8: 9.69, 9: 10.38, 10: 11.06, 11: 11.74, 12: 12.40,
};

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const generatePixPayload = (amount: number) => {
  const key = PIX_KEY;
  const name = "SR Software";
  const city = "Linhares";
  const value = amount.toFixed(2);

  const pad = (id: string, val: string) => `${id}${String(val.length).padStart(2, "0")}${val}`;

  const gui = pad("00", "br.gov.bcb.pix");
  const chave = pad("01", key);
  const mai = pad("26", gui + chave);
  const mcc = pad("52", "0000");
  const currency = pad("53", "986");
  const amount_ = pad("54", value);
  const country = pad("58", "BR");
  const merchantName = pad("59", name.substring(0, 25));
  const merchantCity = pad("60", city.substring(0, 15));
  const payloadFormat = pad("00", "01");

  const payload = payloadFormat + mai + mcc + currency + amount_ + country + merchantName + merchantCity + "6304";

  let crc = 0xFFFF;
  for (let i = 0; i < payload.length; i++) {
    crc ^= payload.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) crc = (crc << 1) ^ 0x1021;
      else crc <<= 1;
      crc &= 0xFFFF;
    }
  }

  return payload + crc.toString(16).toUpperCase().padStart(4, "0");
};

interface InsuranceCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const InsuranceCheckoutDialog = ({ open, onOpenChange }: InsuranceCheckoutDialogProps) => {
  const closeDialog = useCallback(() => onOpenChange(false), [onOpenChange]);
  useBackButton(open, closeDialog, "insurance-checkout");
  const { toast } = useToast();
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("pix");
  const [creditInstallments, setCreditInstallments] = useState(1);
  const [finalTotal, setFinalTotal] = useState(0);

  const creditRate = creditRates[creditInstallments] / 100;
  const creditTotal = INSURANCE_PRICE * (1 + creditRate);
  const creditParcelaValue = creditTotal / creditInstallments;

  const copyPixCode = () => {
    const pixPayload = generatePixPayload(finalTotal);
    navigator.clipboard.writeText(pixPayload);
    toast({ title: "Código Pix copiado!", description: "Cole no app do seu banco para pagar." });
  };

  const handleSubmit = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      toast({ title: "Preencha seus dados", description: "Nome e telefone são obrigatórios.", variant: "destructive" });
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    const total = selectedMethod === "pix" ? INSURANCE_PRICE : creditTotal;

    try {
      const { error } = await supabase.from("orders").insert({
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        payment_method: selectedMethod,
        items: [{ id: "seguro-premium", name: "Seguro Premium - Furto e Roubo", price: INSURANCE_PRICE, qty: 1 }],
        total,
        status: "pending",
        notes: "Contratação de seguro avulso",
      });

      if (error) {
        if (error.message?.includes("duplicado") || error.message?.includes("duplicate")) {
          toast({ title: "Pedido já registrado", description: "Aguarde alguns minutos.", variant: "destructive" });
        } else {
          toast({ title: "Erro ao salvar pedido", variant: "destructive" });
        }
        setSubmitting(false);
        return;
      }

      setFinalTotal(total);
      setSubmitted(true);
    } catch {
      toast({ title: "Erro ao salvar pedido", variant: "destructive" });
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSubmitted(false);
    setSubmitting(false);
    setCustomerName("");
    setCustomerPhone("");
    setSelectedMethod("pix");
    setCreditInstallments(1);
    setFinalTotal(0);
  };

  // === AFTER CONFIRMATION ===
  if (submitted) {
    if (selectedMethod === "pix") {
      const pixPayload = generatePixPayload(finalTotal);
      return (
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-foreground text-center">Pagamento via Pix</DialogTitle>
            </DialogHeader>
            <div className="text-center space-y-4">
              <CheckCircle className="w-12 h-12 text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">Seguro registrado! Escaneie o QR Code para pagar.</p>
              <p className="text-2xl font-bold text-gradient-gold">{formatCurrency(finalTotal)}</p>
              <div className="bg-white p-4 rounded-lg inline-block">
                <QRCodeSVG value={pixPayload} size={220} />
              </div>
              <button
                onClick={copyPixCode}
                className="flex items-center gap-2 mx-auto px-4 py-2 bg-secondary rounded-lg text-sm text-foreground hover:bg-secondary/80 transition-colors"
              >
                <Copy className="w-4 h-4" /> Copiar Pix Copia e Cola
              </button>
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                <p className="flex items-center gap-1 justify-center"><Clock className="w-3 h-3" /> Aguardando confirmação do pagamento</p>
                <p>Após o pagamento, entraremos em contato pelo WhatsApp.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="bg-card border-border max-w-md">
          <div className="text-center py-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-primary mx-auto" />
            <h3 className="font-display text-2xl font-bold text-foreground">Seguro Contratado!</h3>
            <p className="text-lg font-semibold text-gradient-gold">{formatCurrency(finalTotal)}</p>
            <p className="text-muted-foreground">Entraremos em contato pelo WhatsApp para confirmar.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // === BEFORE CONFIRMATION ===
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground">Contratar Seguro Premium</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
            <p className="text-sm font-semibold text-foreground">Seguro Furto e Roubo</p>
            <p className="text-2xl font-bold text-gradient-gold">{formatCurrency(INSURANCE_PRICE)}<span className="text-sm font-normal text-muted-foreground">/mês</span></p>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              placeholder="Seu nome completo"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={100}
            />
            <input
              type="tel"
              placeholder="WhatsApp (ex: 27 99999-9999)"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={20}
            />
          </div>

          <Tabs value={selectedMethod} onValueChange={setSelectedMethod}>
            <TabsList className="w-full bg-secondary">
              <TabsTrigger value="pix" className="flex-1 text-xs gap-1"><QrCode className="w-3 h-3" />Pix</TabsTrigger>
              <TabsTrigger value="credito" className="flex-1 text-xs gap-1"><CreditCard className="w-3 h-3" />Crédito</TabsTrigger>
            </TabsList>

            <TabsContent value="pix" className="space-y-3">
              <div className="text-center space-y-2">
                <p className="text-2xl font-bold text-gradient-gold">{formatCurrency(INSURANCE_PRICE)}</p>
                <p className="text-sm text-muted-foreground">O QR Code para pagamento será gerado após confirmar o pedido.</p>
              </div>
            </TabsContent>

            <TabsContent value="credito" className="space-y-3">
              <p className="text-2xl font-bold text-gradient-gold text-center">{formatCurrency(creditTotal)}</p>
              <p className="text-xs text-center text-muted-foreground">
                {creditInstallments}x de {formatCurrency(creditParcelaValue)} ({creditRates[creditInstallments]}%)
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Parcelas:</p>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <button
                      key={n}
                      onClick={() => setCreditInstallments(n)}
                      className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                        creditInstallments === n
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-secondary border-border text-foreground hover:border-primary"
                      }`}
                    >
                      {n}x
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">Parcelamento via maquininha.</p>
            </TabsContent>
          </Tabs>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-3 rounded-lg bg-gradient-gold text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Enviando..." : "Confirmar Contratação"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InsuranceCheckoutDialog;
